import React, { useState, useContext } from 'react';
import { ImagePlus, X, AlertTriangle, Clock, Zap } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { cn } from "../../lib/utils";
import axios from "axios";
import { BASELINE } from "../../util";
import { UserContext } from '../../context/UserContext';

const priorities = [
  { value: "low", label: "Low", icon: <Clock className="w-4 h-4" />, color: "bg-blue-100 text-blue-700" },
  { value: "medium", label: "Medium", icon: <AlertTriangle className="w-4 h-4" />, color: "bg-yellow-100 text-yellow-700" },
  { value: "high", label: "High", icon: <Zap className="w-4 h-4" />, color: "bg-red-100 text-red-700" },
];

export function FeedbackForm({ onSubmit }) {
  const [images, setImages] = useState([]);
  const [priority, setPriority] = useState("low");
  const maxImages = 5;
  const fileInputRef = React.useRef(null);
  const { user } = useContext(UserContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const context = e.target.context.value;

    // Create a FormData instance
    const formData = new FormData();
    formData.append("user_id", user.u_id);
    formData.append("created_time", new Date().toISOString());
    formData.append("status", "open");
    formData.append("priority", priority);
    formData.append("context", context);

    // Append each file under the field "images"
    images.forEach((img) => {
      formData.append("images", img.file);
    });

    try {
      // Send the feedback data to the /openticket endpoint using multipart/form-data
      await axios.post(`${BASELINE}openticket`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      alert("Feedback submitted successfully!");

      // Reset the form fields and component state
      e.target.reset(); // Reset native form fields
      setPriority("low");
      setImages([]);
      if (onSubmit) onSubmit(formData);
    } catch (error) {
      alert("Failed to submit feedback. Please try again.");
    }
  };

  const handleImageUpload = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Create an array of objects containing both the file and a temporary URL for previewing
      const newImages = Array.from(files).map((file) => ({
        url: URL.createObjectURL(file),
        file: file,
      }));

      const availableSlots = maxImages - images.length;
      const imagesToAdd = newImages.slice(0, availableSlots);
      setImages((prev) => [...prev, ...imagesToAdd]);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteImage = (indexToDelete) => {
    setImages((prev) => {
      const newImages = prev.filter((_, index) => index !== indexToDelete);
      URL.revokeObjectURL(prev[indexToDelete].url);
      return newImages;
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold mb-4">Priority</h2>
          <div className="flex gap-4">
            {priorities.map((p) => (
              <Button
                key={p.value}
                type="button"
                onClick={() => setPriority(p.value)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-6",
                  p.color,
                  priority === p.value
                    ? "ring-2 ring-offset-2 ring-offset-background"
                    : "hover:brightness-95 transition-all",
                  "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background"
                )}
              >
                {p.icon}
                {p.label}
              </Button>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-4">Context</h2>
          <Textarea
            placeholder="Describe your feedback..."
            className="min-h-[150px] resize-none"
            name="context"
            required
          />
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-4">Images</h2>
          <div className="flex gap-4 flex-wrap">
            {images.map((image, index) => (
              <div key={index} className="relative h-24 w-24 group">
                <img
                  src={image.url || "/placeholder.svg"}
                  alt={`Upload ${index + 1}`}
                  className="h-full w-full rounded-md object-cover"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleDeleteImage(index)}
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Remove image</span>
                </Button>
              </div>
            ))}

            {images.length < maxImages && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="h-24 w-24 border-dashed"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ImagePlus className="h-8 w-8 text-muted-foreground" />
                </Button>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </>
            )}
          </div>
          {images.length > 0 && (
            <p className="text-sm text-muted-foreground mt-2">
              {images.length} of {maxImages} images uploaded
            </p>
          )}
        </div>
      </div>

      <Button type="submit" className="w-full text-lg py-6">
        Submit Feedback
      </Button>
    </form>
  );
}
